import React from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { Bot, BotDocument } from '../lib/types';
import { tint } from '../lib/tone';
import { WARN_TEXT, OVERLINE, RADIUS, TONE } from '../lib/brand';
import { RecRow, SCAN_LABEL, fmtBytes, isDownloadable, sortDocs, sortRecs } from '../lib/botPage';
import { verdictChips } from '../lib/selectors';
import { fmtIct } from '../lib/ict';
import { NoReviewChip, PriorityChip, ProvLine, VerdictChip } from '../chips';
import { Heartbeat } from './Heartbeat';

const Block = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Box component="section" sx={{ mb: 2.5 }}>
    <Typography variant="overline" sx={{ ...OVERLINE, display: 'block', fontSize: 10.5, mb: 0.6 }}>{title}</Typography>
    {children}
  </Box>
);

export function OverviewTab({
  bot, rows, docs, detailReady, onOpenRec, onTab,
}: {
  bot: Bot; rows: RecRow[]; docs: BotDocument[]; detailReady: boolean; onOpenRec: (id: string) => void; onTab: (tab: string) => void;
}) {
  const latestRecs = sortRecs(rows, 'date', 'desc').slice(0, 5);
  const latestDocs = sortDocs(docs, 'modified', 'desc').slice(0, 5);
  const chips = verdictChips(bot);
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 3 }}>
      <Box>
        <Block title="Activity heartbeat"><Heartbeat activity={bot.activity} /></Block>
        <Block title="Doing now">
          <Typography sx={{ fontSize: 13.5 }}>{(bot.doing && bot.doing.text) || 'No status file found'}</Typography>
          {bot.doing && <ProvLine prov={bot.doing.prov} fmt={fmtIct} />}
        </Block>
        {(bot.blockers || []).length > 0 && (
          <Block title="Blockers">
            {(bot.blockers || []).map((b) => (
              <Box key={b.text} sx={{ border: `1px solid ${TONE.bad.border}`, bgcolor: tint(TONE.bad.bg), borderRadius: RADIUS.lg, p: 1.2, mb: 0.8 }}>
                <Typography sx={{ fontSize: 13 }}>{b.text}</Typography>
                <ProvLine prov={b.prov} fmt={fmtIct} />
              </Box>
            ))}
          </Block>
        )}
        <Block title="C-suite verdicts">
          {chips.length === 0 ? <NoReviewChip /> : <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap' }}>{chips.map((c) => <VerdictChip key={`${c.exec}${c.verdict}`} exec={c.exec} verdict={c.verdict} />)}</Box>}
        </Block>
      </Box>
      <Box>
        <Block title="Latest recommendations">
          {!detailReady ? <Typography variant="body2" color="text.secondary">Loading…</Typography>
            : latestRecs.length === 0 ? <Typography variant="body2" color="text.secondary">No dated recommendation files found.</Typography>
              : latestRecs.map((r) => (
                <ButtonBase key={r.rec.id} onClick={() => onOpenRec(r.rec.id)} data-testid="overview-rec" sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', textAlign: 'left', width: '100%', py: 0.8, borderTop: '1px solid', borderColor: 'divider' }}>
                  <PriorityChip priority={r.rec.priority_code || 'Unscored'} />
                  <Typography sx={{ fontSize: 12.5, flex: 1 }}>{r.rec.title}</Typography>
                  <Typography variant="caption" color="text.secondary">{r.rec.date || ''}</Typography>
                </ButtonBase>
              ))}
          {detailReady && rows.length > 5 && <ButtonBase onClick={() => onTab('recommendations')} sx={{ mt: 0.8, fontSize: 12, color: 'primary.main', textDecoration: 'underline' }}>All {rows.length} recommendations</ButtonBase>}
        </Block>
        <Block title="Latest documents">
          {!detailReady ? <Typography variant="body2" color="text.secondary">Loading…</Typography>
            : latestDocs.length === 0 ? <Typography variant="body2" color="text.secondary">No documents found.</Typography>
              : latestDocs.map((d) => (
                <Box key={d.sha256 + d.path} sx={{ display: 'flex', gap: 1, py: 0.7, borderTop: '1px solid', borderColor: 'divider', fontSize: 12.5 }}>
                  <Box sx={{ flex: 1, minWidth: 0, wordBreak: 'break-word' }}>{d.title}</Box>
                  <Typography variant="caption" color="text.secondary">{fmtBytes(d.size_bytes)}</Typography>
                  <Typography variant="caption" sx={{ color: isDownloadable(d) ? 'text.secondary' : WARN_TEXT }}>{isDownloadable(d) ? fmtIct(d.modified_at) : SCAN_LABEL[d.scan.status] || 'Withheld'}</Typography>
                </Box>
              ))}
          {detailReady && docs.length > 5 && <ButtonBase onClick={() => onTab('documents')} sx={{ mt: 0.8, fontSize: 12, color: 'primary.main', textDecoration: 'underline' }}>All {docs.length} documents</ButtonBase>}
        </Block>
      </Box>
    </Box>
  );
}
